/**
 * PWA Offline — AES-GCM Token Encryption
 *
 * Design decisions (from design.md):
 * - AES-GCM 256-bit via Web Crypto API
 * - Key derived from SHA-256(userId + deviceId) — in-memory only, never persisted
 * - 16-byte random IV per encryption
 *
 * The encrypted blob stored in IndexedDB is opaque to an attacker without
 * the in-memory key (derived from userId + deviceId, never persisted).
 */

// ── Types ─────────────────────────────────────────────────────────────────────

export interface EncryptedPayload {
    iv: string; // Base64-encoded AES-GCM IV (16 bytes)
    ciphertext: string; // Base64-encoded encrypted token
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Convert an ArrayBuffer to a URL-safe Base64 string.
 */
function bufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    // Use URL-safe base64 (replace +/ with -_)
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Convert a URL-safe Base64 string to an ArrayBuffer.
 */
function base64ToBuffer(base64: string): ArrayBuffer {
    // Restore standard base64
    let normalized = base64.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if needed
    while (normalized.length % 4 !== 0) {
        normalized += '=';
    }
    const binary = atob(normalized);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

// ── Key Derivation ────────────────────────────────────────────────────────────

/**
 * Derives an AES-GCM CryptoKey from userId and deviceId.
 *
 * The derivation uses SHA-256 to hash "userId:deviceId" and then imports
 * the hash output as key material for AES-GCM. The derived key stays in
 * memory only — never persisted to storage.
 *
 * @param userId   - The numeric user ID
 * @param deviceId - A device-specific string (e.g., fingerprint or UUID)
 * @returns A CryptoKey suitable for AES-GCM encrypt/decrypt
 */
export async function deriveKey(userId: number, deviceId: string): Promise<CryptoKey> {
    // Combine userId and deviceId into a single string
    const keyMaterial = `${userId}:${deviceId}`;

    // Encode to UTF-8
    const encoder = new TextEncoder();
    const data = encoder.encode(keyMaterial);

    // Hash with SHA-256 to get 32 bytes of key material
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Import the hash as AES-GCM key material
    const cryptoKey = await crypto.subtle.importKey(
        'raw', // format: raw bytes
        hashBuffer, // key material (32 bytes from SHA-256)
        { name: 'AES-GCM', length: 256 }, // algorithm descriptor
        false, // extractable — we keep the key in memory
        ['encrypt', 'decrypt'] // allowed usages
    );

    return cryptoKey;
}

// ── Encryption ────────────────────────────────────────────────────────────────

/**
 * Encrypts a plaintext token using AES-GCM with a random 16-byte IV.
 *
 * @param plaintext - The raw token string to encrypt
 * @param key       - An AES-GCM CryptoKey (derived from userId + deviceId)
 * @returns An EncryptedPayload with base64-encoded iv and ciphertext
 */
export async function encryptToken(plaintext: string, key: CryptoKey): Promise<EncryptedPayload> {
    const encoder = new TextEncoder();
    const data = encoder.encode(plaintext);

    // Generate a random 16-byte IV for AES-GCM
    const iv = crypto.getRandomValues(new Uint8Array(16));

    // Encrypt using AES-GCM
    const ciphertextBuffer = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        data
    );

    return {
        iv: bufferToBase64(iv.buffer),
        ciphertext: bufferToBase64(ciphertextBuffer),
    };
}

// ── Decryption ────────────────────────────────────────────────────────────────

/**
 * Decrypts an AES-GCM encrypted token.
 *
 * @param iv         - Base64-encoded IV (from encryptToken)
 * @param ciphertext - Base64-encoded ciphertext (from encryptToken)
 * @param key        - The AES-GCM CryptoKey used for encryption
 * @returns The original plaintext token
 * @throws  If decryption fails (wrong key, tampered data, etc.)
 */
export async function decryptToken(iv: string, ciphertext: string, key: CryptoKey): Promise<string> {
    const ivBuffer = base64ToBuffer(iv);
    const ciphertextBuffer = base64ToBuffer(ciphertext);

    const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: new Uint8Array(ivBuffer) },
        key,
        ciphertextBuffer
    );

    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
}
